import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  UserPlus,
  ShieldCheck,
  Activity,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileCode2,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import api from '../../services/api';
import ArchitecturePanel from '../../components/admin/ArchitecturePanel';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PendingSummary {
  membership_requests: number;
  suspended_orgs: number;
  inactive_users_7d: number;
}

interface RecentActivity {
  id: string;
  action_type: string;
  performed_by_name?: string;
  performed_by_email?: string;
  target_user_name?: string;
  target_user_email?: string;
  organization_name?: string;
  created_at: string;
}

interface QuickStat {
  label: string;
  value: number | string;
  sub?: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  to: string;
}

// ─── Action type display map (reused from UserManagementAuditPage) ────────────
const ACTION_LABELS: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  user_role_changed:            { label: 'Role Changed',            icon: ShieldCheck,  color: 'text-blue-600 dark:text-blue-400' },
  user_added_to_org:            { label: 'Added to Org',            icon: CheckCircle,  color: 'text-green-600 dark:text-green-400' },
  user_removed_from_org:        { label: 'Removed from Org',        icon: XCircle,      color: 'text-orange-600 dark:text-orange-400' },
  user_removed_from_platform:   { label: 'Deleted from Platform',   icon: XCircle,      color: 'text-red-600 dark:text-red-400' },
  membership_request_approved:  { label: 'Membership Approved',     icon: CheckCircle,  color: 'text-green-600 dark:text-green-400' },
  membership_request_rejected:  { label: 'Membership Rejected',     icon: XCircle,      color: 'text-red-600 dark:text-red-400' },
  user_invited:                 { label: 'User Invited',            icon: UserPlus,     color: 'text-purple-600 dark:text-purple-400' },
  user_activated:               { label: 'User Activated',          icon: CheckCircle,  color: 'text-green-600 dark:text-green-400' },
  user_deactivated:             { label: 'User Deactivated',        icon: AlertTriangle,color: 'text-gray-500 dark:text-gray-400' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PlatformAdminDashboardPage() {
  const [pending, setPending] = useState<PendingSummary | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [platformStats, setPlatformStats] = useState<{ total_organizations: number; total_users: number; active_users_30d: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archOpen, setArchOpen] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [membershipRes, statsRes, auditRes] = await Promise.all([
        api.get('/membership-requests', { params: { status: 'pending' } }),
        api.get('/organizations/analytics/platform-stats'),
        api.get('/platform/users/audit-logs', { params: { limit: 10 } }),
      ]);

      const pendingRequests = (membershipRes.data?.data ?? []).length;
      const stats = statsRes.data?.data ?? {};

      setPlatformStats({
        total_organizations: stats.total_organizations ?? 0,
        total_users: stats.total_users ?? 0,
        active_users_30d: stats.active_users_30d ?? 0,
      });

      setPending({
        membership_requests: pendingRequests,
        suspended_orgs: 0,   // not yet exposed by API — placeholder
        inactive_users_7d: (stats.total_users ?? 0) - (stats.active_users_30d ?? 0),
      });

      setRecentActivity(auditRes.data?.data ?? []);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load dashboard';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Build quick stat cards once data is available
  const quickStats: QuickStat[] = platformStats ? [
    {
      label: 'Organisations',
      value: platformStats.total_organizations,
      icon: Building2,
      iconBg: 'bg-blue-100 dark:bg-blue-900/40',
      iconColor: 'text-blue-600 dark:text-blue-400',
      to: '/platform-admin/organizations',
    },
    {
      label: 'Total Users',
      value: platformStats.total_users,
      sub: `${platformStats.active_users_30d} active (30d)`,
      icon: Users,
      iconBg: 'bg-green-100 dark:bg-green-900/40',
      iconColor: 'text-green-600 dark:text-green-400',
      to: '/platform-admin/users',
    },
    {
      label: 'Pending Requests',
      value: pending?.membership_requests ?? 0,
      sub: 'awaiting review',
      icon: UserPlus,
      iconBg: pending?.membership_requests ? 'bg-amber-100 dark:bg-amber-900/40' : 'bg-gray-100 dark:bg-gray-800',
      iconColor: pending?.membership_requests ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400',
      to: '/platform-admin/membership-requests',
    },
    {
      label: 'Platform Analytics',
      value: '→',
      sub: 'API calls, response time, errors',
      icon: Activity,
      iconBg: 'bg-purple-100 dark:bg-purple-900/40',
      iconColor: 'text-purple-600 dark:text-purple-400',
      to: '/platform-admin/analytics',
    },
  ] : [];

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Platform Overview</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Pending actions and recent activity across all organisations
          </p>
        </div>
        {/* Unobtrusive architecture reference link */}
        <button
          type="button"
          onClick={() => setArchOpen(true)}
          className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors mt-1 flex-shrink-0"
          title="View solution architecture reference"
        >
          <FileCode2 className="w-3.5 h-3.5" />
          <span>Architecture</span>
        </button>
      </div>

      <ArchitecturePanel isOpen={archOpen} onClose={() => setArchOpen(false)} />

      {error && (
        <Alert type="error" onClose={() => setError(null)}>{error}</Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
            {quickStats.map((s) => (
              <Link key={s.label} to={s.to} className="block group h-full">
                <Card className="h-full">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        {s.label}
                      </p>
                      <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                        {s.value}
                      </p>
                      {s.sub && (
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{s.sub}</p>
                      )}
                    </div>
                    <div className={`p-2 rounded-lg ${s.iconBg} flex-shrink-0 ml-3`}>
                      <s.icon className={`w-5 h-5 ${s.iconColor}`} />
                    </div>
                  </div>
                  <div className="mt-3 flex items-center text-xs text-blue-600 dark:text-blue-400 group-hover:underline">
                    <span>View</span>
                    <ChevronRight className="w-3 h-3 ml-0.5" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          {/* Pending Actions */}
          {pending && pending.membership_requests > 0 && (
            <Card>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Pending Actions
              </h2>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                <Link
                  to="/platform-admin/membership-requests"
                  className="flex items-center justify-between py-3 hover:bg-gray-50 dark:hover:bg-gray-800 -mx-6 px-6 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <UserPlus className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {pending.membership_requests} membership request{pending.membership_requests !== 1 ? 's' : ''} awaiting review
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Users waiting to join an organisation
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </Link>
              </div>
            </Card>
          )}

          {/* Recent Activity */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                Recent Activity
              </h2>
              <Link
                to="/platform-admin/user-audit"
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-0.5"
              >
                View full audit
                <ChevronRight className="w-3 h-3" />
              </Link>
            </div>

            {recentActivity.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                No recent activity
              </p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {recentActivity.map((entry) => {
                  const meta = ACTION_LABELS[entry.action_type] ?? {
                    label: entry.action_type.replace(/_/g, ' '),
                    icon: Activity,
                    color: 'text-gray-500',
                  };
                  const Icon = meta.icon;
                  return (
                    <div key={entry.id} className="flex items-start gap-3 py-3">
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.color}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-white">
                          <span className="font-medium">
                            {entry.performed_by_name || entry.performed_by_email || 'System'}
                          </span>
                          {' · '}
                          <span className="text-gray-600 dark:text-gray-300">{meta.label}</span>
                          {(entry.target_user_name || entry.target_user_email) && (
                            <span className="text-gray-500 dark:text-gray-400">
                              {' for '}
                              {entry.target_user_name || entry.target_user_email}
                            </span>
                          )}
                        </p>
                        {entry.organization_name && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {entry.organization_name}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
                        {timeAgo(entry.created_at)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Quick Links */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Organisation Directory', sub: 'Provision and manage tenants', to: '/platform-admin/organizations', icon: Building2 },
              { label: 'Role Registry', sub: 'Manage permissions and RBAC', to: '/platform-admin/roles', icon: ShieldCheck },
              { label: 'Vertical Marketplace', sub: 'Enable modules per organisation', to: '/platform-admin/verticals', icon: Activity },
            ].map((link) => (
              <Link key={link.to} to={link.to} className="block group">
                <Card>
                  <div className="flex items-center gap-3">
                    <link.icon className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {link.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{link.sub}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-400 ml-auto flex-shrink-0 transition-colors" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Made with Bob
